import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { deleteField, deleteDoc, doc, limit, onSnapshot, orderBy, query, updateDoc, where, collection } from "firebase/firestore";
import { onValue, ref, set } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { Bell, Check, CheckCircle2, Megaphone, MessageSquare, ShieldAlert, Trash2, UserCheck, X } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { cloudFunctions, db, realtimeDb } from "../firebase/config";
const NotificationType = {
    Message: 100,
    ManagerRequest: 200,
    Advertisement: 300,
    Admin: 400,
};
function formatNotificationDate(value) {
    if (!value)
        return "";
    return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value));
}
function isRead(notification, userNotificationStates) {
    return userNotificationStates[notification.id] === "read";
}
export function isManagerRequest(notification) {
    return notification.type === NotificationType.ManagerRequest && Boolean(notification.teamId);
}
function isAdvertisement(notification) {
    return notification.type === NotificationType.Advertisement;
}
function safeExternalUrl(value) {
    if (!value)
        return "";
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:" ? url.toString() : "";
    }
    catch {
        return "";
    }
}
function notificationIcon(notification) {
    switch (notification.type) {
        case NotificationType.ManagerRequest:
            return UserCheck;
        case NotificationType.Advertisement:
            return Megaphone;
        case NotificationType.Admin:
            return ShieldAlert;
        case NotificationType.Message:
        default:
            return MessageSquare;
    }
}
export async function markNotificationRead(userId, notificationId) {
    if (!userId || !notificationId)
        return;
    await set(ref(realtimeDb, `userNotifications/${userId}/${notificationId}`), "read");
}
export async function acceptManagerInvitation(notification) {
    if (!notification.teamId)
        throw new Error("This manager invitation is missing its team.");
    const callable = httpsCallable(cloudFunctions, "acceptManagerRequest");
    await callable({ teamId: notification.teamId });
    await deleteDoc(doc(db, "notifications", notification.id));
}
export async function declineManagerInvitation(userId, notification) {
    if (!userId || !notification.teamId)
        throw new Error("This manager invitation is missing its team.");
    await updateDoc(doc(db, "users", userId), { [`teams.${notification.teamId}`]: deleteField() });
    await deleteDoc(doc(db, "notifications", notification.id));
}
export function useNotificationCenter() {
    const { user, profile } = useAuth();
    const userId = profile?.id || user?.uid || "";
    const [notifications, setNotifications] = useState([]);
    const [notificationsLoading, setNotificationsLoading] = useState(true);
    const [notificationsError, setNotificationsError] = useState("");
    const [userStateError, setUserStateError] = useState("");
    const [userStateLoading, setUserStateLoading] = useState(true);
    const [userNotificationStates, setUserNotificationStates] = useState({});
    useEffect(() => {
        if (!userId) {
            setUserNotificationStates({});
            setUserStateError("");
            setUserStateLoading(false);
            return;
        }
        setUserStateError("");
        setUserNotificationStates({});
        setUserStateLoading(true);
        const userNotificationsRef = ref(realtimeDb, `userNotifications/${userId}`);
        return onValue(userNotificationsRef, (snapshot) => {
            const value = snapshot.val();
            const states = {};
            if (value && typeof value === "object") {
                Object.entries(value).forEach(([notificationId, state]) => {
                    if (state === "read" || state === "dismiss")
                        states[notificationId] = state;
                });
            }
            setUserNotificationStates(states);
            setUserStateError("");
            setUserStateLoading(false);
        }, (caughtError) => {
            console.error("Notification preference listener failed", caughtError);
            setUserStateError("Read and dismiss preferences could not be synchronized.");
            setUserStateLoading(false);
        });
    }, [userId]);
    useEffect(() => {
        if (!userId) {
            setNotifications([]);
            setNotificationsLoading(false);
            return;
        }
        setNotificationsLoading(true);
        setNotificationsError("");
        const notificationsQuery = query(collection(db, "notifications"), where("userId", "in", [userId, "app"]), orderBy("timeStamp", "desc"), limit(10));
        return onSnapshot(notificationsQuery, (snapshot) => {
            setNotifications(snapshot.docs.map((notificationDocument) => ({ id: notificationDocument.id, ...notificationDocument.data() })));
            setNotificationsLoading(false);
        }, (caughtError) => {
            setNotificationsError(caughtError.message);
            setNotificationsLoading(false);
        });
    }, [userId]);
    const visibleNotifications = useMemo(() => notifications
        .filter((notification) => userNotificationStates[notification.id] !== "dismiss")
        .sort((left, right) => Number(isManagerRequest(right)) - Number(isManagerRequest(left)) || (right.timeStamp ?? 0) - (left.timeStamp ?? 0)), [notifications, userNotificationStates]);
    const unreadCount = useMemo(() => visibleNotifications.filter((notification) => !isRead(notification, userNotificationStates)).length, [visibleNotifications, userNotificationStates]);
    return { userId, notifications: visibleNotifications, loading: notificationsLoading, error: notificationsError, userStateError, userStateLoading, userNotificationStates, unreadCount };
}
export function useUnreadNotificationsCount() {
    return useNotificationCenter().unreadCount;
}
export function NotificationsPage() {
    const { userId, notifications, loading, error, userStateError, userNotificationStates } = useNotificationCenter();
    const [busyId, setBusyId] = useState("");
    const [actionError, setActionError] = useState("");
    async function markRead(notification) {
        if (!userId)
            return;
        setBusyId(notification.id);
        setActionError("");
        try {
            await markNotificationRead(userId, notification.id);
        }
        catch (caughtError) {
            setActionError(caughtError instanceof Error ? caughtError.message : "Notification could not be marked as read.");
        }
        finally {
            setBusyId("");
        }
    }
    async function dismissNotification(notification) {
        if (!userId)
            return;
        setBusyId(notification.id);
        setActionError("");
        try {
            await set(ref(realtimeDb, `userNotifications/${userId}/${notification.id}`), "dismiss");
        }
        catch (caughtError) {
            setActionError(caughtError instanceof Error ? caughtError.message : "Notification could not be dismissed.");
        }
        finally {
            setBusyId("");
        }
    }
    async function acceptManagerRequest(notification) {
        setBusyId(notification.id);
        setActionError("");
        try {
            await acceptManagerInvitation(notification);
        }
        catch (caughtError) {
            setActionError(caughtError instanceof Error ? caughtError.message : "Manager request could not be accepted.");
        }
        finally {
            setBusyId("");
        }
    }
    async function declineManagerRequest(notification) {
        if (!userId)
            return;
        setBusyId(notification.id);
        setActionError("");
        try {
            await declineManagerInvitation(userId, notification);
        }
        catch (caughtError) {
            setActionError(caughtError instanceof Error ? caughtError.message : "Manager request could not be declined.");
        }
        finally {
            setBusyId("");
        }
    }
    return _jsxs("div", { className: "content notifications-page", children: [_jsx("section", { className: "page-heading notifications-heading", children: _jsxs("div", { children: [_jsx("h1", { children: "Notifications" }), _jsx("p", { children: "Manager requests, app updates, and messages." })] }) }), actionError && _jsxs("div", { className: "details-state details-state--error", children: [_jsx("strong", { children: "Notification action failed" }), _jsx("span", { children: actionError })] }), userStateError && _jsxs("div", { className: "details-state details-state--error", children: [_jsx("strong", { children: "Notification preferences could not be loaded" }), _jsx("span", { children: userStateError })] }), loading && _jsxs("div", { className: "details-state", children: [_jsx(Bell, { size: 24 }), _jsx("strong", { children: "Loading notifications..." })] }), !loading && error && _jsxs("div", { className: "details-state details-state--error", children: [_jsx("strong", { children: "Notifications could not be loaded" }), _jsx("span", { children: error })] }), !loading && !error && notifications.length === 0 && _jsxs("div", { className: "details-state", children: [_jsx(Bell, { size: 25 }), _jsx("strong", { children: "No notifications" }), _jsx("span", { children: "You are all caught up." })] }), !loading && !error && notifications.length > 0 && _jsx("section", { className: "notifications-list", "aria-label": "Notifications feed", children: notifications.map((notification) => {
                    const read = isRead(notification, userNotificationStates);
                    const managerRequest = isManagerRequest(notification);
                    const advertisement = isAdvertisement(notification);
                    const adUrl = safeExternalUrl(notification.url);
                    const busy = busyId === notification.id;
                    const Icon = notificationIcon(notification);
                    return _jsxs("article", { className: `notification-card ${read ? "notification-card--read" : ""}`, children: [_jsx("div", { className: "notification-card__icon", children: _jsx(Icon, { size: 20 }) }), _jsxs("div", { className: "notification-card__body", children: [_jsxs("div", { className: "notification-card__title-row", children: [_jsx("h2", { children: notification.title || "Notification" }), !read && _jsx("span", { children: "Unread" })] }), _jsx("p", { children: notification.message || "" }), advertisement && notification.image && _jsx("a", { className: "notification-card__ad-image", href: adUrl || undefined, target: "_blank", rel: "noreferrer", "aria-label": notification.urlText || notification.title || "Open advertisement", onClick: (event) => { if (!adUrl)
                                            event.preventDefault(); }, children: _jsx("img", { src: notification.image, alt: notification.title || "Advertisement" }) }), advertisement && adUrl && _jsx("a", { className: "notification-card__ad-link", href: adUrl, target: "_blank", rel: "noreferrer", children: notification.urlText || adUrl }), _jsx("small", { children: formatNotificationDate(notification.timeStamp) }), managerRequest && notification.userId !== "app" && _jsxs("div", { className: "notification-card__actions notification-card__actions--primary", children: [_jsxs("button", { type: "button", disabled: busy, onClick: () => void acceptManagerRequest(notification), children: [_jsx(CheckCircle2, { size: 15 }), "Accept"] }), _jsxs("button", { type: "button", disabled: busy, onClick: () => void declineManagerRequest(notification), children: [_jsx(X, { size: 15 }), "Decline"] })] })] }), !managerRequest && _jsxs("div", { className: "notification-card__actions", children: [!read && _jsxs("button", { type: "button", disabled: busy, onClick: () => void markRead(notification), title: "Mark as read", children: [_jsx(Check, { size: 15 }), _jsx("span", { children: "Read" })] }), _jsxs("button", { type: "button", disabled: busy, onClick: () => void dismissNotification(notification), title: "Dismiss", children: [_jsx(Trash2, { size: 15 }), _jsx("span", { children: "Dismiss" })] })] })] }, notification.id);
                }) })] });
}
